import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { select, Store } from '@ngrx/store'
import {
  Channel,
  ChannelNameTokens,
  DataCheckerService,
  RateMap,
  Rental,
  selectRentalEntities,
  Toaster,
  untilDestroy,
} from '@tokeet/tv3-platform'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormControl } from '@angular/forms'
import { ChannelGuard } from '@tv3/guards/channel.guard'
import * as R from 'ramda'
import { catchError, concatMap, distinctUntilChanged, map, startWith, tap, toArray } from 'rxjs/operators'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { ActionFailed, isEmptyTable, localeCompareSort } from '@tokeet-frontend/utility'
import { SelectableRow } from '@tokeet-frontend/utility'
import { UserStorage } from '@tv3/services/utils/storage.service'
import { TableService } from '@tv3/services/utils/table.service'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { MatPaginator } from '@angular/material/paginator'
import { ConnectionGuard } from '@tv3/guards/connection.guard'
import { selectAllAPIConnectionViews } from '@tv3/store/connection/connection.selectors'
import { BehaviorSubject, combineLatest, EMPTY, of } from 'rxjs'
import * as lodash from 'lodash'
import { ConnectionHelperService } from '@tv3/containers/channels/channel-connect/connection-helper.service'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import { HttpErrorResponse } from '@angular/common/http'
import { RentalRatesDialogService } from '../rental-rates-dialog/rental-rates-dialog.service'
import { RentalRatesDialogTab } from '../rental-rates-dialog/rental-rates-dialog.component'
import { PushRatesToApiConnectionPayload } from '@tv3/store/connection/connection.types'
import { distinctUntilChangedById } from '@tv3/utils/operators/distinct-by-id'

interface PushConnectionRateItem {
  id: string
  channel: Channel
  rental: Rental
  connection: ConnectionView
  status?: 'requesting' | 'scheduled' | 'done' | 'error'
  errorText?: string

  rentalName: string
  rentalAddress: string
  channelName: string
  propertyName: string

  rateMappings: RateMap[]

  isRateMappingImplicit: boolean
  isLinked: boolean
  isPushRatesEnabled: boolean
  isPushAvailabilityEnabled: boolean
  isRentalBaseRateReady: boolean
  isDoable: boolean
}

const implicitRateMappingChannelsName = [ChannelNameTokens.AirBnBV2API]
const ratesPushEnabledChannelsName = [
  ChannelNameTokens.ExpediaAPI,
  ChannelNameTokens.AgodaAPI,
  ChannelNameTokens.BookingAPI,
  ChannelNameTokens.AirBnBV2API,
  ChannelNameTokens.Tiket,
  ChannelNameTokens.Trip,
]

@Component({
  selector: 'app-push-rates-overlay',
  templateUrl: './push-rates-overlay.component.html',
  styleUrls: ['./push-rates-overlay.component.scss'],
})
export class PushRatesOverlayComponent extends SelectableRow<PushConnectionRateItem> implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  dataSource = new MatTableDataSource<PushConnectionRateItem>()
  isEmptyTable$ = isEmptyTable(this.dataSource)

  channelFiltersForm = this.fb.group({
    all: [],
  })

  channels$ = new BehaviorSubject<Channel[]>([])

  displayedColumns = ['select', 'channelName', 'rentalName', 'propertyName', 'status', 'expandCtrl']
  expandedRowItem: PushConnectionRateItem
  isExpandableRow = (i: number, row: PushConnectionRateItem) => {
    return !row.isRateMappingImplicit && row.isLinked && row.isRentalBaseRateReady
  }

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private toaster: Toaster,
    protected storage: UserStorage,
    protected tableService: TableService,
    private dialogRef: MatDialogRef<PushRatesOverlayComponent>,
    private dataCheckerService: DataCheckerService,
    private connectionService: ConnectionService,
    private connectionHelperService: ConnectionHelperService,
    private rentalRatesDialog: RentalRatesDialogService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super(tableService, storage, TableType.PushAllRentals)
    this.dataCheckerService.check([ChannelGuard, ConnectionGuard])
  }

  ngOnInit() {
    this.dataSource.paginator = this.paginator
    if (this.savedSort) this.sort.sort(this.savedSort)
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort
    this.setDataSource(this.dataSource)
    combineLatest([
      this.channelFiltersForm.valueChanges.pipe(
        startWith({}),
        map((selected: { [key: string]: boolean }) =>
          lodash.map(selected, (v, k) => (v ? k : '')).filter((c) => !!c && c !== 'all')
        )
      ),
      this.store.pipe(select(selectRentalEntities)),
      this.store.pipe(
        select(selectAllAPIConnectionViews),
        map((items) => R.filter((c) => !!c.rentalId, items)), // linked
        map((items) => R.filter((c) => ratesPushEnabledChannelsName.includes(c.channel?.name), items)) // enabled
      ),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([selectedChannels, rentalsById, connections]) => {
        this.channels$.next(
          lodash.uniqBy(
            lodash.map(connections, (t) => t.channel),
            (c) => c.id
          )
        )

        connections = !selectedChannels.length
          ? connections
          : lodash.filter(connections, (c) => {
              const channel = c.channel
              return selectedChannels.includes(channel.name)
            })

        const items = lodash.map(connections, (conn): PushConnectionRateItem => {
          const rental = rentalsById[conn.rentalId]
          const channel = conn.channel
          const isLinked = !!rental
          const isRateMappingImplicit = implicitRateMappingChannelsName.includes(channel?.name)
          const isPushRatesEnabled = isLinked && (rental?.rateMap?.length > 0 || isRateMappingImplicit)
          const isPushAvailabilityEnabled = isLinked

          let rateMappings: RateMap[] = []

          if (implicitRateMappingChannelsName.includes(channel.name)) {
            rateMappings = this.getImplicitRateMappings(conn, channel)
          } else {
            rateMappings = lodash.filter(rental?.rateMap, (r) => {
              return r.api === channel.name && r.propertyId == conn.propertyId && r.roomId == conn.roomId
            })
          }

          return {
            id: conn.id,
            rental: rental,
            channel: channel,
            connection: conn,
            rentalName: rental?.name || 'N/A',
            rentalAddress: rental?.address?.country || 'N/A',
            propertyName: conn.roomName,
            channelName: channel?.friendlyName,
            rateMappings,
            isLinked,
            isRateMappingImplicit,
            isPushRatesEnabled,
            isPushAvailabilityEnabled,
            isRentalBaseRateReady: this.isRentalBaseRateReady(rental),
            isDoable: isRateMappingImplicit ? isLinked : isPushAvailabilityEnabled && isPushRatesEnabled,
          }
        })

        this.dataSource.data = items
      })

    this.channels$.pipe(untilDestroy(this), distinctUntilChangedById()).subscribe((channels: Channel[]) => {
      R.forEach((channel) => {
        this.channelFiltersForm.addControl(channel.name, new FormControl())
      }, channels)
    })

    this.channelFiltersForm
      .get('all')
      .valueChanges.pipe(distinctUntilChanged(), untilDestroy(this))
      .subscribe((allSelected) => {
        const formKeys = R.keys(this.channelFiltersForm.getRawValue()) as string[]
        const trueValues = R.times(() => true, R.keys(this.channelFiltersForm.getRawValue()).length)
        const falseValues = R.times(() => false, R.keys(this.channelFiltersForm.getRawValue()).length)

        if (allSelected) {
          this.channelFiltersForm.patchValue(R.zipObj(formKeys, trueValues))
        } else {
          this.channelFiltersForm.patchValue(R.zipObj(formKeys, falseValues))
        }
      })
  }

  getImplicitRateMappings(connection: ConnectionView, channel: Channel): RateMap[] {
    if (!implicitRateMappingChannelsName.includes(channel.name)) {
      return []
    }
    return [
      {
        category: 'default',
        rateId: connection.propertyId as string,
        roomId: connection.roomId as number,
        priceModel: null,
        maxGuest: null,
        maxPersons: null,
        propertyId: connection.propertyId as number,
        rentalId: connection.rentalId,
        channelId: connection.channelId,
        api: channel.name,
      } as RateMap,
    ]
  }

  isRowSelectable(item: PushConnectionRateItem) {
    return item.isPushRatesEnabled && item.isPushAvailabilityEnabled
  }

  isRentalBaseRateReady(rental: Rental) {
    const baseRate = rental?.baseRate
    return baseRate && baseRate.nightly > 0
  }

  getItemWarningInfo(item: PushConnectionRateItem) {
    if (!item.isLinked) {
      return 'This property should be linked to a Tokeet rental.'
    }
    if (!item.isRentalBaseRateReady) {
      return `A Base Rate must be set in order to properly push rates to your connected channels. Please click rental and enter the required values.`
    }
    if (item.isRateMappingImplicit) {
      return
    }
    if (!item.isPushRatesEnabled) {
      return `Please create rate mappings for this property within Tokeet. Rate mappings added in Rategenie will not be considered.`
    }
  }

  onRentalClick(item: PushConnectionRateItem) {
    if (!item.rental) {
      return
    }
    this.rentalRatesDialog.openSide({ rental: item.rental, tab: RentalRatesDialogTab.Settings })
  }

  onItemClick(item: PushConnectionRateItem) {
    if (!this.isExpandableRow(0, item)) {
      return
    }
    this.expandedRowItem = this.expandedRowItem?.id === item.id ? null : item
  }

  onSearch(term: string) {
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  close() {
    this.dialogRef.close()
  }

  resetRequestStatusForAll() {
    const items = this.dataSource.data
    lodash.forEach(items, (item) => {
      item.status = undefined
    })
  }

  onLink(item: PushConnectionRateItem) {
    this.connectionHelperService.linkRental(item.channel?.name, item.connection).subscribe()
  }

  private pushRates(rateMappings: RateMap[]) {
    return of(...rateMappings).pipe(
      concatMap((rateMap) => {
        const payload: PushRatesToApiConnectionPayload = {
          category: rateMap.category,
          rateid: rateMap.rateId,
          maxguest: rateMap.maxGuest,
          pricemodel: rateMap.priceModel,
          roomId: rateMap.roomId,
          propertyId: rateMap.propertyId,
          rental_id: rateMap.rentalId,
          maxPersons: rateMap.maxPersons,
        }

        return this.connectionService.pushRates(rateMap.api as ChannelNameTokens, rateMap.channelId, payload)
      }),
      toArray()
    )
  }

  onPushRate() {
    const items = this.getSelected()
    if (!items.length) {
      this.toaster.warning("You don't select any rental.")
      return
    }
    this.resetRequestStatusForAll()
    of(...items)
      .pipe(
        concatMap((item) => {
          item.status = 'requesting'
          return this.pushRates(item.rateMappings).pipe(
            tap(() => {
              item.status = 'done'
            }),
            catchError((res: HttpErrorResponse) => {
              this.store.dispatch(ActionFailed({ error: res }))
              item.status = 'error'
              item.errorText = res?.error?.error || ''
              return EMPTY
            })
          )
        }),
        toArray()
      )
      .subscribe(() => {
        this.toaster.success(
          'All rental rates are pushed, please check status column to know the result for each rental.'
        )
      })
  }

  onPushAvailability() {
    const items = this.getSelected()
    if (!items.length) {
      this.toaster.warning("You don't select any rental.")
      return
    }
    this.resetRequestStatusForAll()
    of(...items)
      .pipe(
        concatMap((item) => {
          const conn = item.connection?.connection
          const channel = item.channel
          item.status = 'requesting'
          return this.connectionService.pushAvailability(channel.name, conn.channelId, conn).pipe(
            tap(() => {
              item.status = 'done'
            }),
            catchError((res: HttpErrorResponse) => {
              if (res?.status !== 424) {
                this.store.dispatch(ActionFailed({ error: res }))
                item.status = 'error'
                item.errorText = res?.error?.error || ''
              } else {
                item.status = 'done'
              }
              return EMPTY
            })
          )
        }),
        toArray()
      )
      .subscribe(() => {
        this.toaster.success(
          'All rentals availability are pushed, please check status column to know the result for each rental.'
        )
      })
  }
}
